import { SpacingProps } from '../../styles/getSpacingSprinkles.js';
import type { SurfaceProps } from '../surface/surface-types.js';
import type {
  LayoutStyles,
  LayoutSizeProps,
  DefaultStyles,
} from '../types/layout.types.js';
import { useLayoutSizeProps } from './useLayoutSizeProps.js';
import { useMarginProps } from './useMarginProps.js';
import { usePaddingProps } from './usePaddingProps.js';

/**
 * Generates layout styles including padding, margin, and size properties.
 * @internal
 */
/* oxlint-disable typescript/no-explicit-any */
export function useLayoutStyles(
  props: SurfaceProps<any>,
  defaultStyles: DefaultStyles,
): LayoutStyles {
  const {
    /** Padding props */
    padding,
    p,
    paddingX,
    px,
    paddingY,
    py,
    paddingTop,
    pt,
    paddingRight,
    pr,
    paddingBottom,
    pb,
    paddingLeft,
    pl,

    /** Margin props */
    margin,
    m,
    marginX,
    mx,
    marginY,
    my,
    marginTop,
    mt,
    marginRight,
    mr,
    marginBottom,
    mb,
    marginLeft,
    ml,

    /** Layout props */
    width,
    minWidth,
    maxWidth,
    height,
    minHeight,
    maxHeight,

    /** filtered remaining props for passing back to component */
    ...remainingProps
  } = props;

  const paddingProps: Pick<
    SpacingProps,
    | 'padding'
    | 'p'
    | 'paddingX'
    | 'px'
    | 'paddingY'
    | 'py'
    | 'paddingTop'
    | 'pt'
    | 'paddingRight'
    | 'pr'
    | 'paddingBottom'
    | 'pb'
    | 'paddingLeft'
    | 'pl'
  > = {
    padding,
    p,
    paddingX,
    px,
    paddingY,
    py,
    paddingTop,
    pt,
    paddingRight,
    pr,
    paddingBottom,
    pb,
    paddingLeft,
    pl,
  };

  const marginProps: Pick<
    SpacingProps,
    | 'margin'
    | 'm'
    | 'marginX'
    | 'mx'
    | 'marginY'
    | 'my'
    | 'marginTop'
    | 'mt'
    | 'marginRight'
    | 'mr'
    | 'marginBottom'
    | 'mb'
    | 'marginLeft'
    | 'ml'
  > = {
    margin,
    m,
    marginX,
    mx,
    marginY,
    my,
    marginTop,
    mt,
    marginRight,
    mr,
    marginBottom,
    mb,
    marginLeft,
    ml,
  };

  const layoutProps: LayoutSizeProps = {
    width,
    minWidth,
    maxWidth,
    height,
    minHeight,
    maxHeight,
  };

  const paddingStyles = usePaddingProps(
    paddingProps,
    defaultStyles.paddingDefaults,
  );
  const marginStyles = useMarginProps(
    marginProps,
    defaultStyles.marginDefaults,
  );
  const layoutSizeStyles = useLayoutSizeProps(layoutProps);

  return {
    paddingStyles,
    marginStyles,
    layoutSizeStyles,
    remainingProps,
  };
}
